
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  // Use different domains based on language
  const domainName = language === 'en' ? 'AttaboyIntelligence.com' : 'AttaboyIntelligenceArtificielle.com';

  return (
    <footer className="bg-gray-50 border-t border-gray-100">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-4">
              <div className="h-10 w-10">
                <svg
                  className="h-full w-full text-attaboy"
                  viewBox="0 0 500 500"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Updated SVG path to match the logo */}
                  <path d="M250 80 L80 400 L420 400 Z" />
                  <circle cx="380" cy="160" r="80" />
                </svg>
              </div>
              <div className="ml-2">
                {language === 'en' ? (
                  <span className="font-poppins text-attaboy-black text-xl font-bold tracking-tight">
                    Attaboy Intelligence
                  </span>
                ) : (
                  <div className="flex flex-col">
                    <span className="font-poppins text-attaboy-black text-xl font-bold tracking-tight">
                      Attaboy
                    </span>
                    <span className="font-poppins text-attaboy text-sm font-medium tracking-tight -mt-1">
                      Intelligence Artificielle
                    </span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-gray-600 mt-4 mb-6">
              {language === 'en' 
                ? 'Making AI radically accessible for businesses of all sizes.' 
                : 'Rendre l\'IA radicalement accessible pour les entreprises de toutes tailles.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-poppins font-semibold text-lg mb-4 text-attaboy-black">
              {language === 'en' ? 'Quick Links' : 'Liens Rapides'}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/products" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('nav.products')}
                </Link>
              </li>
              <li>
                <Link to="/solutions" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('nav.solutions')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-poppins font-semibold text-lg mb-4 text-attaboy-black">
              {language === 'en' ? 'Legal' : 'Mentions Légales'}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-600 hover:text-attaboy transition-colors">
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-poppins font-semibold text-lg mb-4 text-attaboy-black">
              {language === 'en' ? 'Contact' : 'Contact'}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <svg
                  className="h-5 w-5 mr-2 text-attaboy"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <a href={`mailto:info@${domainName}`} className="text-gray-600 hover:text-attaboy transition-colors">
                  info@{domainName}
                </a>
              </li>
              <li className="flex items-center">
                <svg
                  className="h-5 w-5 mr-2 text-attaboy"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-600">
                  {language === 'en' ? 'Montreal, Canada' : 'Montréal, Canada'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center md:flex md:justify-between md:text-left">
          <p className="text-gray-500">
            <a 
              href={`https://${domainName}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-attaboy transition-colors"
            >
              &copy; {currentYear} Attaboy Intelligence. {t('footer.rights')}
            </a>
          </p>
          <div className="mt-4 md:mt-0 flex justify-center md:justify-end space-x-6">
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-attaboy transition-colors"
              aria-label="Twitter"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
              </svg>
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-attaboy transition-colors"
              aria-label="LinkedIn"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.7 3H4.3A1.3 1.3 0 003 4.3v15.4A1.3 1.3 0 004.3 21h15.4a1.3 1.3 0 001.3-1.3V4.3A1.3 1.3 0 0019.7 3zM8.339 18.338H5.667v-8.59h2.672v8.59zM7.004 8.574a1.548 1.548 0 11-.002-3.096 1.548 1.548 0 01.002 3.096zm11.335 9.764H15.67v-4.177c0-.996-.017-2.278-1.387-2.278-1.389 0-1.601 1.086-1.601 2.206v4.249h-2.667v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.779 3.203 4.092v4.711z"></path>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
